import { gql } from "@apollo/client";
import { CellEditRequestEvent } from "ag-grid-enterprise";
import { message } from "utils/ciq-message";
import { InfoFeatureType } from "change-events/change-event-polling";
import { SelectCellEditorV2 } from "components/cell-editor/select-cell-editor";
import {
  CompanyParticipationType,
  ErrorMessages,
  EUserTypes,
  UserParticipationType
} from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import {
  MUTATION_BULK_EDIT_FEATURE_INSTANCES,
  SAVE_LOCATION_MATERIALS,
  UPDATE_FEATURE_BID_PACKAGE_BY_PK,
  UPDATE_FEATURE_COMPANY_PARTICIPANT,
  UPDATE_FEATURE_COMPANY_PARTICIPANT_WITH_EMPTY_USERS,
  UPDATE_FEATURE_INSTANCE_BY_PK,
  UPDATE_FEATURE_INSTANCES,
  UPDATE_FEATURE_MATERIALS,
  UPDATE_FEATURE_SHIPPING_LOCATION,
  UPDATE_FEATURE_SUBMITTALS
} from "entity-app/graphQL/ciq-feature-mutations";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";
import { userEntityCellRenderer } from "entity-app/shared-components/log-render-components/cell-renders";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { useProjectParticipants } from "hooks/project-participants";
import { MeasurementSelectEditor } from "components/cell-editor/measurement-select-editor";
import { APPLY_WORKFLOW_TEMPLATE } from "pages/preconstruction-details/components/workflow-template-dropdown/query/mutation";
import { useContext } from "react";
import {
  isProjectInIntegrationMode,
  matchUserProjectRole,
  validatorForFieldValue
} from "utils/utils";
import { getCurrentDuration } from "entity-app/shared-components/entity-milestones-duration-columns";
import { getMilestoneBySequenceNo } from "entity-app/shared-components/entity-milestones-duration-columns/feature-date-block-columns";
import { isNaN } from "lodash";
import { DateUtils } from "utils/dateutils";
import {
  linkBidPackages,
  TOnsiteEstimateUpdateSummary
} from "services/bid-package-links";
import { useUpdateUserParticipants } from "./update-user-participants";

const emptyAllowedFields = new Set([
  "submittal_number",
  "manual_entry_date",
  "onsite_estimate_date",
  "_custom_fields.custom_field_enum_1",
  "_custom_fields.custom_field_enum_2",
  "_custom_fields.custom_field_enum_3",
  "_custom_fields.custom_field_enum_4",
  "_custom_fields.custom_field_enum_5",
  "_custom_fields.custom_field_date_1",
  "_custom_fields.custom_field_date_2",
  "_custom_fields.custom_field_date_3",
  "_custom_fields.custom_field_date_4",
  "_custom_fields.custom_field_date_5",
  "_custom_fields.custom_field_text_1",
  "_custom_fields.custom_field_text_2",
  "_custom_fields.custom_field_text_3",
  "_custom_fields.custom_field_text_4",
  "_custom_fields.custom_field_text_5",
  "_custom_fields.custom_field_number_1",
  "_custom_fields.custom_field_number_2",
  "_custom_fields.custom_field_number_3",
  "_custom_fields.custom_field_number_4",
  "_custom_fields.custom_field_number_5"
]);

const getErrorText = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return ErrorMessages.SomethingWentWrong;
};

const getFeatureType = (type: InfoFeatureType) => {
  if (type === InfoFeatureType.submittal) {
    return 1;
  }
  if (type === InfoFeatureType.material) {
    return 2;
  }
  if (type === InfoFeatureType.bidPackage) {
    return 4;
  }
  if (type === InfoFeatureType.designPackage) {
    return 5;
  }
  return null;
};

const uniqueErrorMessage = (
  infoFeatureType: InfoFeatureType,
  colId: string
): string => {
  switch (infoFeatureType) {
    case InfoFeatureType.designPackage:
      return colId === "number"
        ? ErrorMessages.DesignPackageNumberMsg
        : ErrorMessages.DesignPackageTitleMsg;
    case InfoFeatureType.bidPackage:
      return colId === "number"
        ? ErrorMessages.BidPackageNumberMsg
        : ErrorMessages.BidPackageTitleMsg;
    case InfoFeatureType.material:
      return ErrorMessages.MaterialExists;
    default:
      return ErrorMessages.SomethingWentWrong;
  }
};

const shouldIgnoreEmptyCellEdit = (event: CellEditRequestEvent<any, any>) =>
  !emptyAllowedFields.has(event.colDef.colId!) &&
  (event.newValue === "" || event.newValue === null);

const buildChangedDataFromCellEdit = (
  event: CellEditRequestEvent<any, any>,
  colId: string,
  fieldId: string,
  existingRowData: any
) => {
  if (event.colDef.cellRenderer === userEntityCellRenderer) {
    if (event.newValue?._custom_fields) {
      return {
        _custom_fields: {
          ...existingRowData._custom_fields,
          ...event.newValue._custom_fields
        }
      };
    }
    return event.newValue;
  }
  if (event.colDef.cellEditor === MeasurementSelectEditor) {
    return {};
  }
  if (event.colDef.cellEditor === SelectCellEditorV2) {
    return {
      [colId]: {
        id: event.newValue?.value || null,
        value: event.newValue?.label
      }
    };
  }
  const changedData: Record<string, any> = {
    [colId]: event.newValue
  };
  if (colId !== fieldId) changedData[fieldId] = event.newValue;
  return changedData;
};

export function useFeatureLogV2InlineEdit({
  refetchListData,
  infoFeatureType,
  skipParticipants = false
}: {
  refetchListData: () => void;
  infoFeatureType: InfoFeatureType;
  skipParticipants?: boolean;
}) {
  const {
    gqlClientForProject,
    tokenRetrievalState,
    projectDetails,
    tokenContents,
    isProjectArchived
  } = useContext(ProjectContext);

  const [mutationUpdateFeatureInstanceFields] = useCIQMutation(
    gql(UPDATE_FEATURE_INSTANCE_BY_PK),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureInstances] = useCIQMutation(
    gql(UPDATE_FEATURE_INSTANCES),
    {
      client: gqlClientForProject
    }
  );

  const [applyWorkflowMutation] = useCIQMutation(APPLY_WORKFLOW_MUTATION, {
    client: gqlClientForProject
  });

  const [mutationUpdateFeatureBidPackageFields] = useCIQMutation(
    gql(UPDATE_FEATURE_BID_PACKAGE_BY_PK),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureMaterialFields] = useCIQMutation(
    gql(UPDATE_FEATURE_MATERIALS),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureCompanyParticipant] = useCIQMutation(
    gql(UPDATE_FEATURE_COMPANY_PARTICIPANT),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureCompanyParticipantWithEmptyUsers] =
    useCIQMutation(gql(UPDATE_FEATURE_COMPANY_PARTICIPANT_WITH_EMPTY_USERS), {
      client: gqlClientForProject
    });

  const [applyWorkflowTemplateMutation] = useCIQMutation(
    APPLY_WORKFLOW_TEMPLATE,
    {
      client: gqlClientForProject
    }
  );

  const [mutationSaveLocationMaterials] = useCIQMutation(
    gql(SAVE_LOCATION_MATERIALS),
    {
      client: gqlClientForProject
    }
  );
  const [mutationUpdateFeatureShippingLocation] = useCIQMutation(
    gql(UPDATE_FEATURE_SHIPPING_LOCATION),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureSubmittalFields] = useCIQMutation(
    gql(UPDATE_FEATURE_SUBMITTALS),
    {
      client: gqlClientForProject
    }
  );
  const [mutationBulkEditFeatureInstances] = useCIQMutation(
    gql(MUTATION_BULK_EDIT_FEATURE_INSTANCES),
    {
      client: gqlClientForProject
    }
  );

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const isSubmittalIntgMode =
    infoFeatureType === InfoFeatureType.submittal && isIntegrationMode;

  const isLoggedInUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const { projectParticipants: WFProjectParticipants } = useProjectParticipants(
    skipParticipants || isSubmittalIntgMode || !isLoggedInUserGC
  );

  const { updateUserAndCompany, updateFeatureUserParticipant } =
    useUpdateUserParticipants(WFProjectParticipants);

  const showMessage = async (
    updateResponse: any,
    revertDataFunc: () => void
  ) => {
    if (updateResponse.success) {
      message.success("Updated successfully.");
      refetchListData();
    } else {
      message.error("Failed to update.");
      revertDataFunc();
    }
  };

  const updateFeatureCompanyParticipantWithEmptyUsers = async ({
    featureInstanceId,
    companyParticipationType,
    userParticipationTypeId,
    vendorIds
  }: {
    featureInstanceId: string;
    companyParticipationType: CompanyParticipationType;
    userParticipationTypeId: UserParticipationType;
    vendorIds: Array<string | null | undefined>;
  }) => {
    // Skip empty vendor ids so clear = delete only (no blank company row)
    const objects = vendorIds
      .filter((id): id is string => Boolean(id))
      .map((id) => ({
        instance_id: featureInstanceId,
        company_participation_type_id: companyParticipationType,
        vendor_id: id
      }));
    const variables: any = {
      instance_id: featureInstanceId,
      company_participation_type_id: companyParticipationType,
      user_participation_type_id: userParticipationTypeId,
      objects
    };
    return mutationUpdateFeatureCompanyParticipantWithEmptyUsers({
      variables
    });
  };

  const updateFeatureCompanyParticipant = async ({
    featureInstanceId,
    companyParticipationType,
    vendorIds
  }: {
    featureInstanceId: string;
    companyParticipationType: CompanyParticipationType;
    vendorIds: Array<string | null | undefined>;
  }) => {
    // Skip empty vendor ids so clear = delete only (no blank company row)
    const objects = vendorIds
      .filter((id): id is string => Boolean(id))
      .map((id) => ({
        instance_id: featureInstanceId,
        company_participation_type_id: companyParticipationType,
        vendor_id: id
      }));
    const variables: any = {
      instance_id: featureInstanceId,
      company_participation_type_id: companyParticipationType,
      objects
    };
    return mutationUpdateFeatureCompanyParticipant({
      variables
    });
  };

  const handleDu2DurationEdit = async (
    event: CellEditRequestEvent<any, any>,
    colId: string,
    revertDataFunc: () => void
  ): Promise<boolean> => {
    if (!/^du2_\d+$/.test(colId)) return false;

    const index = parseInt(colId.split("_")[1], 10);
    const dataWithMilestonesAndOffsets = structuredClone(event.data);
    const currentDuration = getCurrentDuration(
      dataWithMilestonesAndOffsets,
      index
    );
    const newDuration = Number(event.newValue);

    if (!currentDuration || isNaN(newDuration) || newDuration < 0) {
      return false;
    }

    currentDuration.duration = event.newValue;
    event.node.setData(dataWithMilestonesAndOffsets);

    const updateResponse = await applyWorkflowMutation({
      variables: {
        input: [
          {
            feature_instance_id: currentDuration.feature_instance_id,
            workflow_instance_id: currentDuration.workflow_instance_id,
            offsets: [
              {
                id: currentDuration.id,
                duration: event.newValue
              }
            ]
          }
        ],
        metadata: {
          request_source: "INLINE_EDIT_LOG_PAGE"
        }
      }
    });

    showMessage(updateResponse, revertDataFunc);
    return true;
  };

  const handleOffsetDurationEdit = async (
    event: CellEditRequestEvent<any, any>,
    colId: string,
    revertDataFunc: () => void
  ): Promise<boolean> => {
    if (!/^duration_offset_\d+$/.test(colId)) return false;

    const sequenceNo = parseInt(colId.split("_")[2], 10);
    const dataWithMilestonesAndOffsets = structuredClone(event.data);
    const currentDuration = getMilestoneBySequenceNo(
      { data: dataWithMilestonesAndOffsets },
      sequenceNo
    );
    const newDuration = Number(event.newValue);

    if (!currentDuration || isNaN(newDuration) || newDuration < 0) {
      return false;
    }

    currentDuration.offset_duration = newDuration;
    event.node.setData(dataWithMilestonesAndOffsets);

    const updateResponse = await applyWorkflowMutation({
      variables: {
        input: [
          {
            feature_instance_id: event.data.id,
            workflow_instance_id:
              event.data?.date_block?.id ||
              event.data.feature_workflow_instance_id,
            offsets: [
              {
                id: currentDuration.offset_id,
                duration: event.newValue
              }
            ]
          }
        ],
        metadata: {
          request_source: "INLINE_EDIT_LOG_PAGE"
        }
      }
    });

    showMessage(updateResponse, revertDataFunc);
    return true;
  };

  const applyTitleOrNumberEdit = async (
    colId: string,
    event: any,
    revertDataFunc: () => void
  ) => {
    if (
      ["number", "name"].includes(colId) &&
      event.newValue === event.oldValue
    ) {
      return;
    }
    if (event?.newValue?.trim() === event?.oldValue?.trim()) {
      return;
    }
    try {
      await validatorForFieldValue(
        event.colDef.field!,
        event.newValue?.trim(),
        infoFeatureType,
        tokenRetrievalState?.token,
        uniqueErrorMessage(infoFeatureType, colId)
      );
    } catch (err) {
      message.error(getErrorText(err));
      revertDataFunc();
      return;
    }

    // Only a title edit that resolves to a known feature type can go
    // through the bulk-edit mutation; everything else patches the field.
    const titleFeatureType = ["name", "title"].includes(colId)
      ? getFeatureType(infoFeatureType)
      : null;

    let updateResponse: any;
    if (titleFeatureType) {
      const variables: any = {
        feature_instance_type_id: titleFeatureType,
        feature_instance_ids: [event.data.id],
        updates: {
          title: event.newValue
        }
      };
      updateResponse = await mutationBulkEditFeatureInstances({
        variables
      });
    } else {
      const variables: any = {
        featureId: event.data.id,
        set: { [event.colDef.field!]: event.newValue }
      };
      updateResponse = await mutationUpdateFeatureInstanceFields({
        variables
      });
    }

    showMessage(updateResponse, revertDataFunc);
  };

  const handleColIdCellEdit = async (
    event: CellEditRequestEvent<any, any>,
    colId: string,
    fieldId: string,
    existingRowData: any,
    changedData: any,
    revertDataFunc: () => void
  ) => {
    switch (colId) {
      case "number":
      case "submittal_number":
      case "name":
      case "title":
        await applyTitleOrNumberEdit(colId, event, revertDataFunc);
        break;
      case "wave":
      case "no_of_bidders":
      case "estimated_value":
      case "awarded_value":
        {
          const variables: any = {
            featureId: event.data.id,
            set: { [event.colDef.field!]: event.newValue }
          };
          const updateResponse = await mutationUpdateFeatureBidPackageFields({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "planned_float":
      case "pre_WF_start_float":
        {
          const input = [
            {
              feature_instance_id: event.data.id,
              workflow_instance_id:
                event.data?.date_block?.id ||
                event.data.feature_workflow_instance_id,
              pre_WF_start_float: event.newValue
            }
          ];

          const updateResponse = await applyWorkflowMutation({
            variables: {
              input,
              metadata: {
                request_source: "INLINE_EDIT_LOG_PAGE"
              }
            }
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "manual_entry_date":
        {
          const input = [
            {
              feature_instance_id: event.data.id,
              workflow_instance_id:
                event.data?.date_block?.id ||
                event.data.feature_workflow_instance_id,
              manual_entry_date: event.newValue
                ? DateUtils.formatDateWithLunchTime(event.newValue)
                : null
            }
          ];

          const updateResponse = await applyWorkflowMutation({
            variables: {
              input,
              metadata: {
                request_source: "INLINE_EDIT_LOG_PAGE"
              }
            }
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "onsite_estimate_date":
        {
          // Bid Package Log: the planning-only activity date. linkBidPackageV2 recomputes
          // Deadline, Next Deadline, floats and risk, so the refetch picks those up.
          const updateResponse = await linkBidPackages(
            {
              bid_package_ids: [event.data.id],
              planning_only_activity_date: event.newValue
                ? DateUtils.formatDateWithLunchTime(event.newValue)
                : null,
              skip_completed: true
            },
            tokenRetrievalState?.token ?? ""
          );
          const summary = updateResponse.data as
            | Partial<TOnsiteEstimateUpdateSummary>
            | undefined;
          if (updateResponse.success && summary?.updated_ids?.length === 0) {
            message.error("This Bid Package cannot be updated.");
            revertDataFunc();
            refetchListData();
            break;
          }
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "bidding_subcontractors":
        {
          const vendorIds = event.newValue.map((x: any) => x.id);
          const updateResponse = await updateFeatureCompanyParticipant({
            featureInstanceId: event.data.id,
            companyParticipationType:
              CompanyParticipationType.BIDDING_SUB_CONTRACTOR,
            vendorIds
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "custom_spec_section":
        {
          const featureType = getFeatureType(infoFeatureType);
          if (!featureType) {
            message.error("Failed to update.");
            revertDataFunc();
            return;
          }

          const variables: any = {
            feature_instance_type_id: featureType,
            feature_instance_ids: [event.data.id],
            updates: {
              spec_sections: event.newValue.map((op: any) => op.id)
            }
          };
          const updateResponse = await mutationBulkEditFeatureInstances({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "preconstruction_lead_id":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.preconstruction_lead_id,
            userParticipationType: UserParticipationType.PRECONSTRUCTION_LEAD
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "gc_representative":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.gc_representative?.id,
            userParticipationType: UserParticipationType.GC_REPRESENTATIVE
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "gc_representative_id":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.gc_representative_id,
            userParticipationType: UserParticipationType.GC_REPRESENTATIVE
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "operations_lead_id":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.operations_lead_id,
            userParticipationType: UserParticipationType.OPERATIONS_LEAD
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "trade_partner":
        {
          const updateResponse =
            await updateFeatureCompanyParticipantWithEmptyUsers({
              featureInstanceId: event.data.id,
              companyParticipationType:
                CompanyParticipationType.RESPONSIBLE_CONTRACTOR,
              vendorIds: [event.newValue.responsible_contractor?.id],
              userParticipationTypeId: UserParticipationType.ASSIGNEE
            });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "responsible_contractor":
        {
          const updateResponse =
            await updateFeatureCompanyParticipantWithEmptyUsers({
              featureInstanceId: event.data.id,
              companyParticipationType:
                CompanyParticipationType.RESPONSIBLE_CONTRACTOR,
              vendorIds: [event.newValue.responsible_contractor?.id],
              userParticipationTypeId: UserParticipationType.SUBMITTER
            });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "design_firm_id":
        {
          const updateResponse =
            await updateFeatureCompanyParticipantWithEmptyUsers({
              featureInstanceId: event.data.id,
              companyParticipationType:
                CompanyParticipationType.DESIGN_FIRM_CONTRACTOR,
              vendorIds: [event.newValue.design_firm_id],
              userParticipationTypeId:
                UserParticipationType.RESPONSIBLE_DESIGNER
            });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "responsible_designer_id":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.responsible_designer_id,
            userParticipationType: UserParticipationType.RESPONSIBLE_DESIGNER
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "assignee":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.assignee_user.id,
            userParticipationType: UserParticipationType.ASSIGNEE
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "awarded_subcontractor_id":
        {
          const updateResponse = await updateFeatureCompanyParticipant({
            featureInstanceId: event.data.id,
            companyParticipationType:
              CompanyParticipationType.AWARDED_SUB_CONTRACTOR,
            vendorIds: [event.newValue.awarded_subcontractor_id]
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "bid_package_template_id":
        {
          const variables = {
            input: [
              {
                feature_instance_id: event.data.id,
                workflow_instance_id: event.data.feature_workflow_instance_id,
                workflow_template_id: event.newValue.bid_package_template_id
              }
            ],
            metadata: {
              request_source: "INLINE_EDIT_LOG_PAGE"
            }
          };

          const updateResponse = await applyWorkflowTemplateMutation({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "date_block_template_name":
        {
          const variables = {
            input: [
              {
                feature_instance_id: event.data.id,
                workflow_instance_id:
                  event.data.date_block?.id ||
                  event.data.feature_workflow_instance_id,
                workflow_template_id: event.newValue.template.id
              }
            ],
            metadata: {
              request_source: "INLINE_EDIT_LOG_PAGE"
            }
          };

          const updateResponse = await applyWorkflowTemplateMutation({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;

      case "purchase_order":
      case "material_tag":
      case "manufacturer":
      case "part_number":
      case "serial_number":
      case "warranty":
        {
          const variables: any = {
            featureId: event.data.id,
            set: {
              [event.colDef.field!]: event.newValue ?? null
            }
          };
          const updateResponse = await mutationUpdateFeatureMaterialFields({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "material_size_unit":
      case "quantity_unit":
        {
          const variables: any = {
            featureId: event.data.id,
            set: {}
          };

          variables.set = {
            [event.newValue.unitValue.prop]: event.newValue.unitValue.value,
            [event.newValue.unitType.prop]: event.newValue.unitType.value
          };

          changedData[event.newValue.unitValue.prop] =
            event.newValue.unitValue.value;

          changedData[event.newValue.unitType.name] = event.newValue.label;

          event.node.setData({ ...existingRowData, ...changedData });

          const updateResponse = await mutationUpdateFeatureMaterialFields({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;

      case "locations":
        {
          const variables = {
            feature_instance_ids: [event.data.id],
            feature_type: infoFeatureType,
            location_ids: event.newValue.map((x: any) => x.id)
          };
          const updateResponse = await mutationSaveLocationMaterials({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;

      case "shipping_location":
        {
          const variables = {
            featureIds: [event.data.id],
            placeId: event.newValue.value
          };
          const updateResponse = await mutationUpdateFeatureShippingLocation({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;

      case "type":
        {
          const variables: any = {
            featureId: event.data.id,
            set: {
              [event.colDef.colId!]: event.newValue?.value || null,
              raw_type: event.newValue?.label || ""
            }
          };
          const updateResponse = await mutationUpdateFeatureSubmittalFields({
            variables
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "gc_reviewer":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.gc_user?.id,
            userParticipationType: UserParticipationType.GC_REVIEWER
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "design_reviewer":
        {
          const mutationUpdateResponse = await updateUserAndCompany({
            userId: event.newValue.design_reviewer?.id,
            featureInstanceId: event.data.id,
            projectParticipants: WFProjectParticipants.designReviewers
          });

          showMessage(mutationUpdateResponse, revertDataFunc);
        }
        break;
      case "submitter":
        {
          const updateResponse = await updateFeatureUserParticipant({
            userId: event.newValue.submitter_reviewer?.id,
            featureInstanceId: event.data.id,
            userParticipationType: UserParticipationType.SUBMITTER
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "watchers":
      case "priority":
        {
          const variables: any = {
            featureId: event.data.id,
            set: { [event.colDef.colId!]: event.newValue }
          };
          const updateResponse = await mutationUpdateFeatureSubmittalFields({
            variables
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "custom_field_date_1":
      case "custom_field_date_2":
      case "custom_field_date_3":
      case "custom_field_date_4":
      case "custom_field_date_5":
      case "custom_field_text_1":
      case "custom_field_text_2":
      case "custom_field_text_3":
      case "custom_field_text_4":
      case "custom_field_text_5":
      case "custom_field_number_1":
      case "custom_field_number_2":
      case "custom_field_number_3":
      case "custom_field_number_4":
      case "custom_field_number_5":
      case "custom_field_enum_1":
      case "custom_field_enum_2":
      case "custom_field_enum_3":
      case "custom_field_enum_4":
      case "custom_field_enum_5":
        {
          const variables: any = {
            feature_instance_type_id: getFeatureType(infoFeatureType),
            feature_instances: [
              {
                id: event.data.id,
                _custom_fields: {
                  [event.colDef.colId!.replace("_custom_fields.", "")]:
                    event.newValue || null
                }
              }
            ]
          };
          const updateResponse = await mutationUpdateFeatureInstances({
            variables
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "custom_field_user_1_obj.id":
      case "custom_field_user_2_obj.id":
      case "custom_field_user_3_obj.id":
      case "custom_field_user_4_obj.id":
      case "custom_field_user_5_obj.id":
        // Handle dynamic custom_field_user_N_obj.id cases (1, 2, 3, etc.)
        // regex always matches the only switch cases (custom_field_user_1..5_obj.id) that reach here; false branch unreachable
        /* v8 ignore next */
        if (/^custom_field_user_\d+_obj\.id$/.test(colId)) {
          const fieldName = event.colDef
            .colId!.replace(/^_custom_fields\./, "")
            .replace(/_obj\.id$/, "");
          // Extract the object key (e.g., "custom_field_user_1_obj")
          const objKey = `${fieldName}_obj`;
          const variables: any = {
            feature_instance_type_id: getFeatureType(infoFeatureType),
            feature_instances: [
              {
                id: event.data.id,
                _custom_fields: {
                  [fieldName]:
                    event.newValue?._custom_fields?.[objKey]?.id || null
                }
              }
            ]
          };
          const updateResponse = await mutationUpdateFeatureInstances({
            variables
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "custom_field_vendor_1_obj.id":
      case "custom_field_vendor_2_obj.id":
      case "custom_field_vendor_3_obj.id":
      case "custom_field_vendor_4_obj.id":
      case "custom_field_vendor_5_obj.id":
        // Handle dynamic custom_field_user_N_obj.id cases (1, 2, 3, etc.)
        // regex always matches the only switch cases (custom_field_vendor_1..5_obj.id) that reach here; false branch unreachable
        /* v8 ignore next */
        if (/^custom_field_vendor_\d+_obj\.id$/.test(colId)) {
          const fieldName = event.colDef
            .colId!.replace(/^_custom_fields\./, "")
            .replace(/_obj\.id$/, "");
          // Extract the object key (e.g., "custom_field_user_1_obj")
          const objKey = `${fieldName}_obj`;
          const variables: any = {
            feature_instance_type_id: getFeatureType(infoFeatureType),
            feature_instances: [
              {
                id: event.data.id,
                _custom_fields: {
                  [fieldName]:
                    event.newValue?._custom_fields?.[objKey]?.id || null
                }
              }
            ]
          };
          const updateResponse = await mutationUpdateFeatureInstances({
            variables
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "custom_field_geolocation_1_obj":
      case "custom_field_geolocation_2_obj":
      case "custom_field_geolocation_3_obj":
      case "custom_field_geolocation_4_obj":
      case "custom_field_geolocation_5_obj":
        // Handle dynamic custom_field_user_N_obj.id cases (1, 2, 3, etc.)
        // regex always matches the only switch cases (custom_field_geolocation_1..5_obj) that reach here; false branch unreachable
        /* v8 ignore next */
        if (/^custom_field_geolocation_\d+_obj$/.test(colId)) {
          const fieldName = event.colDef
            .colId!.replace(/^_custom_fields\./, "")
            .replace(/_obj$/, "");

          const variables: any = {
            feature_instance_type_id: getFeatureType(infoFeatureType),
            feature_instances: [
              {
                id: event.data.id,
                _custom_fields: {
                  [fieldName]: event.newValue?.value || null
                }
              }
            ]
          };
          const updateResponse = await mutationUpdateFeatureInstances({
            variables
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      default:
        break;
    }
  };

  const onCellEditRequest = async (event: CellEditRequestEvent<any, any>) => {
    if (isProjectArchived || shouldIgnoreEmptyCellEdit(event)) return;

    const colId = event.colDef.colId!.replace("_custom_fields.", "");
    const fieldId =
      event.colDef.field?.replace("_custom_fields.", "") || "field";
    const existingRowData = { ...event.data };
    const revertDataFunc = () => {
      event.node.setData({ ...existingRowData });
    };
    const changedData = buildChangedDataFromCellEdit(
      event,
      colId,
      fieldId,
      existingRowData
    );

    event.node.setData({ ...existingRowData, ...changedData });

    if (await handleDu2DurationEdit(event, colId, revertDataFunc)) return;
    if (await handleOffsetDurationEdit(event, colId, revertDataFunc)) return;

    await handleColIdCellEdit(
      event,
      colId,
      fieldId,
      existingRowData,
      changedData,
      revertDataFunc
    );
  };

  return { onCellEditRequest };
}
